/**
 * Group-by summarization for the attribute-table workspace.
 */
import type { VectorExportFeature } from '@geosoft/viewer-sdk';

export type AggregateFn = 'count' | 'sum' | 'mean' | 'min' | 'max';

export interface AggregateRequest {
  readonly field: string;
  readonly fn: Exclude<AggregateFn, 'count'>;
}

export interface SummarizeSpec {
  readonly groupBy: string;
  readonly aggregates?: ReadonlyArray<AggregateRequest>;
}

export interface SummarizeResult {
  readonly columns: string[];
  readonly rows: VectorExportFeature[];
}

const COUNT_COLUMN = 'COUNT';

export function summarizeAttributeRows(
  rows: ReadonlyArray<VectorExportFeature>,
  spec: SummarizeSpec,
): SummarizeResult {
  const groups = new Map<string, VectorExportFeature[]>();
  for (const row of rows) {
    const key = stringifyGroup(row.properties?.[spec.groupBy]);
    const bucket = groups.get(key);
    if (bucket) bucket.push(row);
    else groups.set(key, [row]);
  }

  const aggregateColumns = (spec.aggregates ?? []).map(
    (entry) => `${entry.fn.toUpperCase()}_${entry.field}`,
  );
  const columns = [spec.groupBy, COUNT_COLUMN, ...aggregateColumns];
  const out: VectorExportFeature[] = [];
  let index = 0;
  for (const [key, bucket] of groups) {
    const properties: Record<string, unknown> = {
      [spec.groupBy]: key === '—' ? null : coerceGroupValue(bucket[0]?.properties?.[spec.groupBy]),
      [COUNT_COLUMN]: bucket.length,
    };
    for (const entry of spec.aggregates ?? []) {
      properties[`${entry.fn.toUpperCase()}_${entry.field}`] = aggregate(
        bucket,
        entry.field,
        entry.fn,
      );
    }
    out.push({
      id: `summary-${index}`,
      properties,
    });
    index += 1;
  }
  out.sort((left, right) => {
    const a = left.properties[COUNT_COLUMN];
    const b = right.properties[COUNT_COLUMN];
    return (typeof b === 'number' ? b : 0) - (typeof a === 'number' ? a : 0);
  });
  return { columns, rows: out };
}

export function numericColumnsForSummarize(
  rows: ReadonlyArray<VectorExportFeature>,
  columns: ReadonlyArray<string>,
  groupBy: string,
): string[] {
  return columns.filter((column) => {
    if (column === groupBy) return false;
    return rows.some((row) => isFiniteNumber(row.properties?.[column]));
  });
}

function aggregate(
  rows: ReadonlyArray<VectorExportFeature>,
  field: string,
  fn: Exclude<AggregateFn, 'count'>,
): number | null {
  const values = rows
    .map((row) => asFiniteNumber(row.properties?.[field]))
    .filter((value): value is number => value !== null);
  if (values.length === 0) return null;
  if (fn === 'sum') return values.reduce((sum, value) => sum + value, 0);
  if (fn === 'mean') return values.reduce((sum, value) => sum + value, 0) / values.length;
  if (fn === 'min') return Math.min(...values);
  return Math.max(...values);
}

function stringifyGroup(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value) ?? '—';
  } catch {
    return String(value);
  }
}

function coerceGroupValue(value: unknown): unknown {
  if (value === null || value === undefined || value === '') return null;
  return value;
}

function asFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function isFiniteNumber(value: unknown): boolean {
  return asFiniteNumber(value) !== null;
}
